import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, FileText, Plus } from "lucide-react";
import { quotesApi } from "@/services/api";
import { useAsync } from "@/hooks/useAsync";
import { fmtMoney, statusTone, prettyStatus } from "@/lib/format";
import PageHeader from "@/components/PageHeader";
import UpgradeBanner from "@/components/UpgradeBanner";
import { LoadingState, ErrorState, EmptyState } from "@/components/DataStates";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

export default function Quotes() {
  const navigate = useNavigate();
  const { data, loading, error, reload } = useAsync(() => quotesApi.list(), []);
  const [q, setQ] = useState("");

  const list = useMemo(() => (Array.isArray(data) ? data : []), [data]);
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return list;
    return list.filter(
      (x) =>
        (x.title || "").toLowerCase().includes(s) ||
        (x.building_name || "").toLowerCase().includes(s) ||
        (x.customer_name || "").toLowerCase().includes(s)
    );
  }, [list, q]);

  return (
    <div>
      <PageHeader title="Quotes" subtitle="Remedial and inspection quotations" actions={<Button onClick={() => navigate("/quotes/new")} data-testid="new-quote-btn"><Plus className="mr-1.5 h-4 w-4" /> New quote</Button>} testId="quotes-header" />
      <UpgradeBanner feature="quotes" className="mb-4" title="Quotes are a Pro feature">Create and send branded remedial &amp; inspection quotes to your clients.</UpgradeBanner>

      {loading && <LoadingState label="Loading quotes…" />}
      {!loading && error && <ErrorState message={error} onRetry={reload} />}

      {!loading && !error && (
        <>
          <div className="relative mb-4 max-w-sm">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input placeholder="Search quotes…" className="pl-10" value={q} onChange={(e) => setQ(e.target.value)} data-testid="quotes-search" />
          </div>

          {filtered.length === 0 ? (
            <EmptyState title={list.length === 0 ? "No quotes yet" : "No matches"} message={list.length === 0 ? "Quotes generated from inspections appear here." : "Try a different search."} testId="quotes-empty" />
          ) : (
            <div className="space-y-2" data-testid="quotes-list">
              {filtered.map((x) => (
                <Card key={x.id} className="flex cursor-pointer items-center justify-between gap-3 p-4 transition-shadow hover:shadow-md" onClick={() => navigate(`/quotes/${x.id}`)} data-testid={`quote-row-${x.id}`}>
                  <div className="flex min-w-0 items-center gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                      <FileText className="h-5 w-5" />
                    </div>
                    <div className="min-w-0">
                      <p className="truncate font-medium text-slate-800">{x.title || "Quote"}</p>
                      <p className="truncate text-sm text-slate-500">{x.building_name || x.customer_name || "—"}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-semibold text-slate-800">{fmtMoney(x.total)}</span>
                    {x.status && <Badge variant="outline" className={statusTone(x.status)}>{prettyStatus(x.status)}</Badge>}
                  </div>
                </Card>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
